import { useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  ChevronsDown,
  Info,
  Trash2,
} from "lucide-react";
import t from "@/lang/lang";
import {
  Position,
  PositionPatch,
  positionErrorKey,
} from "@/services/backend/htz/position/types/position";
import { AirHandlingUnit } from "@/services/backend/htz/ahu/types/air-handling-unit";
import {
  ETableCell,
  ETableCellCheckbox,
  ETableCellDecimalInput,
  ETableCellInput,
  ETableHead,
  ETableHeader,
  ETableRow,
} from "@/shared/components/tables/e-table";
import { Button } from "@/shared/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/shared/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { EditorInput } from "@/shared/components/editor/editor-input";
import { DecimalNumberInputField } from "@/shared/components/field/input-field";
import { FieldErrors } from "@/shared/components/field/field";
import {
  useFieldErrors,
  useHasFieldErrors,
} from "@/shared/lib/errors/err-validation-failed-context";
import { cn } from "@/shared/lib/utils";

/**
 * PositionsTableHeader is the header of a table of {@link PositionRow}s.
 */
export function PositionsTableHeader() {
  return (
    <ETableHeader>
      <ETableRow>
        <ETableHead>{t("Pos")}</ETableHead>
        <ETableHead className="w-full">{t("Titel")}</ETableHead>
        <ETableHead>{t("Anlagen")}</ETableHead>
        <ETableHead>{t("Anzahl")}</ETableHead>
        <ETableHead>{t("Preis / Anzahl")}</ETableHead>
        <ETableHead>{t("Preis Angebot")}</ETableHead>
        <ETableHead>{t("Optional?")}</ETableHead>
        <ETableHead>{t("Aktionen")}</ETableHead>
      </ETableRow>
    </ETableHeader>
  );
}

/**
 * PositionRow edits a position of an offer, order or confirmation. Each
 * change is reported as a patch, the document holding the position saves
 * it. Its fields highlight and reset the errors of the keys
 * `positions.<id>.<field>` in the nearest ErrValidationFailedContextProvider.
 */
export function PositionRow({
  position,
  onPatch,
  onRemove,
  airHandlingUnits,
  disabled = false,
}: {
  position: Position;
  onPatch: (patch: PositionPatch) => void;
  onRemove: () => void;
  airHandlingUnits: AirHandlingUnit[];
  disabled?: boolean;
}) {
  return (
    <>
      <ETableRow className="border-b-0">
        <ETableCell>{position.number}</ETableCell>
        <ETableCellInput
          value={position.title}
          onChange={(e) => onPatch({ title: e.target.value })}
          fieldName={positionErrorKey(position, "title")}
          disabled={disabled}
        />
        <AirHandlingUnitCell
          position={position}
          onPatch={onPatch}
          airHandlingUnits={airHandlingUnits}
          disabled={disabled}
        />
        <ETableCellInput
          type="number"
          value={position.unitCount}
          onChange={(e) =>
            onPatch({ unitCount: parseInt(e.target.value, 10) || 0 })
          }
          fieldName={positionErrorKey(position, "unitCount")}
          disabled={disabled}
          className="w-20"
        />
        <ETableCellDecimalInput
          value={position.unitPrice}
          onValueChange={(unitPrice) => onPatch({ unitPrice })}
          fieldName={positionErrorKey(position, "unitPrice")}
          disabled={disabled}
          className="w-28"
        />
        <TotalPriceCell position={position} />
        <ETableCellCheckbox
          checked={position.optional}
          onCheckedChange={(checked) => onPatch({ optional: checked === true })}
          disabled={disabled}
        />
        <ETableCell className="text-right">
          <div className="flex justify-end gap-1">
            <UnitHoursDialog
              position={position}
              onPatch={onPatch}
              disabled={disabled}
            />
            <Button
              variant="destructive"
              className="h-8 w-8 p-0"
              onClick={onRemove}
              disabled={disabled}
            >
              <Trash2 className="h-5 w-5" />
            </Button>
          </div>
        </ETableCell>
      </ETableRow>
      <DescriptionRow
        position={position}
        onPatch={onPatch}
        disabled={disabled}
      />
    </>
  );
}

function DescriptionRow({
  position,
  onPatch,
  disabled,
}: {
  position: Position;
  onPatch: (patch: PositionPatch) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { errors, invalid, reset } = useFieldErrors(
    positionErrorKey(position, "description"),
  );

  return (
    <>
      <ETableRow className={open ? "border-b-0" : ""}>
        <ETableCell aria-hidden="true" />
        <ETableCell colSpan={7} className="h-6 py-0 pb-1">
          <Button
            variant="ghost"
            onClick={() => setOpen(!open)}
            aria-invalid={invalid || undefined}
            className={cn("h-6 px-1.5", invalid && "text-destructive")}
          >
            {open ? (
              <ChevronDown className="mr-2 h-4 w-4" />
            ) : (
              <ChevronRight className="mr-2 h-4 w-4" />
            )}
            <span>{t("Details")}</span>
          </Button>
        </ETableCell>
      </ETableRow>
      {open && (
        <ETableRow>
          <ETableCell aria-hidden="true" />
          <ETableCell colSpan={7} className="pt-2">
            <EditorInput
              initialHtml={position.description}
              onHtmlChange={(description) => {
                reset();
                onPatch({ description });
              }}
              invalid={invalid}
              disabled={disabled}
              debounce
            />
            <FieldErrors errors={errors} />
          </ETableCell>
        </ETableRow>
      )}
    </>
  );
}

function AirHandlingUnitCell({
  position,
  onPatch,
  airHandlingUnits,
  disabled,
}: {
  position: Position;
  onPatch: (patch: PositionPatch) => void;
  airHandlingUnits: AirHandlingUnit[];
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selected = position.airHandlingUnits ?? [];
  const { invalid, reset } = useFieldErrors(
    positionErrorKey(position, "airHandlingUnits"),
  );

  const onSelect = (ahuId: string) => () => {
    reset();
    onPatch({
      airHandlingUnits: selected.includes(ahuId)
        ? selected.filter((id) => id !== ahuId)
        : [...selected, ahuId],
    });
  };

  return (
    <ETableCell>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="h-8 justify-between"
            disabled={disabled}
            aria-invalid={invalid || undefined}
          >
            <span>
              {t("Anlagen")} ({selected.length})
            </span>
            <ChevronsDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0">
          <Command>
            <CommandInput placeholder={t("Suchen")} />
            <CommandEmpty>{t("Nicht gefunden.")}</CommandEmpty>
            <CommandGroup>
              {airHandlingUnits.map((ahu) => (
                <CommandItem
                  key={ahu.id}
                  value={ahu.name}
                  onSelect={onSelect(ahu.id)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selected.includes(ahu.id) ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {ahu.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </ETableCell>
  );
}

function TotalPriceCell({ position }: { position: Position }) {
  // Read-only: derived from the unit count and price.
  const { invalid } = useFieldErrors(positionErrorKey(position, "totalPrice"));

  return (
    <ETableCell className="text-right font-bold">
      <span
        className={cn(
          invalid &&
            "border-destructive rounded-lg border-2 bg-red-200 px-1.5 py-1",
        )}
      >
        {position.totalPrice.toLocaleString("de", { minimumFractionDigits: 2 })}
      </span>
    </ETableCell>
  );
}

function UnitHoursDialog({
  position,
  onPatch,
  disabled,
}: {
  position: Position;
  onPatch: (patch: PositionPatch) => void;
  disabled: boolean;
}) {
  const { hasErrors } = useHasFieldErrors([
    positionErrorKey(position, "unitHours"),
    positionErrorKey(position, "totalHours"),
  ]);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          aria-invalid={hasErrors || undefined}
        >
          <Info className="h-5 w-5" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Details")}</DialogTitle>
        </DialogHeader>
        <DecimalNumberInputField
          label={t("Stunden pro Einheit")}
          value={position.unitHours}
          onValueChange={(unitHours) => onPatch({ unitHours })}
          fieldName={positionErrorKey(position, "unitHours")}
          disabled={disabled}
        />
        <div className="text-muted-foreground text-sm">
          {t("Gesamtstunden")}:{" "}
          {position.totalHours.toLocaleString("de", {
            minimumFractionDigits: 2,
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
